import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/shared/Common";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";

const HOURS = ["12a","1a","2a","3a","4a","5a","6a","7a","8a","9a","10a","11a","12p","1p","2p","3p","4p","5p","6p","7p","8p","9p","10p","11p"];
const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

export default function Analytics() {
  const [heatmap, setHeatmap] = useState([]);
  const [aging, setAging] = useState([]);
  const [gap, setGap] = useState([]);
  const [util, setUtil] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [productivity, setProd] = useState([]);

  useEffect(() => {
    (async () => {
      const [h, a, g, u, l, p] = await Promise.all([
        api.get("/analytics/heatmap"),
        api.get("/analytics/queue-aging"),
        api.get("/analytics/skill-gap"),
        api.get("/analytics/team-utilization"),
        api.get("/analytics/leaderboard"),
        api.get("/analytics/productivity-trend"),
      ]);
      setHeatmap(h.data.grid);
      setAging(a.data.items);
      setGap(g.data.items);
      setUtil(u.data.items);
      setLeaderboard(l.data.items);
      setProd(p.data.items);
    })();
  }, []);

  const maxHeat = Math.max(1, ...heatmap.flat());
  const heatColor = (v) => {
    if (!v) return "hsl(220, 15%, 22%)";
    const intensity = v / maxHeat;
    return `hsl(221, 100%, ${Math.max(30, 60 - intensity * 30)}%)`;
  };

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Heatmaps, trends, forecasts and skill gap insights" />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <div className="border rounded-md bg-card p-5">
          <div className="eyebrow text-muted-foreground">Intake heatmap</div>
          <div className="text-sm font-medium mb-4">Cases created by day × hour</div>
          <div className="overflow-x-auto">
            <div className="min-w-max">
              <div className="grid" style={{ gridTemplateColumns: `40px repeat(24, minmax(20px, 1fr))` }}>
                <div></div>
                {HOURS.map(h => <div key={h} className="text-[9px] text-muted-foreground text-center mono">{h}</div>)}
                {DAYS.map((d, dIdx) => (
                  <div key={`row-${d}`} className="contents">
                    <div className="text-[10px] text-muted-foreground mono flex items-center">{d}</div>
                    {(heatmap[dIdx] || Array(24).fill(0)).map((v, hIdx) => (
                      <div
                        key={`${d}-${hIdx}`}
                        className="aspect-square rounded-sm border border-black/10"
                        style={{ background: heatColor(v) }}
                        title={`${d} ${HOURS[hIdx]}: ${v}`}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="border rounded-md bg-card p-5">
          <div className="eyebrow text-muted-foreground">Queue aging</div>
          <div className="text-sm font-medium mb-4">Open cases by age</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={aging}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="bucket" fontSize={11} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <YAxis fontSize={11} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="count" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <div className="border rounded-md bg-card p-5">
          <div className="eyebrow text-muted-foreground">Skill gap</div>
          <div className="text-sm font-medium mb-4">Demand (pending) vs supply (available)</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={gap} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" fontSize={11} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <YAxis dataKey="skill" type="category" fontSize={11} tick={{ fill: "hsl(var(--muted-foreground))" }} width={90} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="demand" fill="hsl(var(--chart-4))" radius={[0, 4, 4, 0]} />
                <Bar dataKey="supply" fill="hsl(var(--chart-2))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border rounded-md bg-card p-5">
          <div className="eyebrow text-muted-foreground">Productivity forecast</div>
          <div className="text-sm font-medium mb-4">Assigned vs completed trend</div>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={productivity}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" fontSize={11} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <YAxis fontSize={11} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="assigned" stroke="hsl(var(--chart-1))" strokeWidth={2} />
                <Line type="monotone" dataKey="completed" stroke="hsl(var(--chart-2))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="border rounded-md bg-card">
        <div className="p-5 border-b">
          <div className="eyebrow text-muted-foreground">Leaderboard</div>
          <div className="text-sm font-medium">Composite performance score</div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-widest text-muted-foreground border-b">
            <tr>
              <th className="px-4 py-3">Rank</th>
              <th className="px-4 py-3">Employee</th>
              <th className="px-4 py-3">Team</th>
              <th className="px-4 py-3">Completed</th>
              <th className="px-4 py-3">Performance</th>
              <th className="px-4 py-3">Quality</th>
              <th className="px-4 py-3">Score</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {leaderboard.map((p, i) => (
              <tr key={p.id} className={i === 0 ? "bg-primary/5" : ""}>
                <td className="px-4 py-3 mono">#{i + 1}</td>
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{p.team}</td>
                <td className="px-4 py-3 mono">{p.completed}</td>
                <td className="px-4 py-3 mono">{p.performance}</td>
                <td className="px-4 py-3 mono">{p.quality}</td>
                <td className="px-4 py-3 mono font-semibold">{p.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
